import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Select } from 'antd'
import { pear } from '@edulastic/constants'
import { segmentApi } from '@edulastic/api'
import {
  IconPDLSparkle,
  IconClose,
  IconBlueQuestionCircle,
} from '@edulastic/icons'
import {
  getAllInterestedCurriculumsSelector,
  getInterestedSubjectsSelector,
  getInterestedGradesSelector,
} from '../../src/selectors/user'
import PearAssistRubricSection from '../common/PearAssistRubricSection'
import {
  getResolvedTestSubjectsSelector,
  getResolvedTestGradesSelector,
  getTestEntitySelector,
} from '../../TestPage/ducks'
import {
  getStandardSetOptions,
  resolveMetadataContext,
} from '../utils/AIQuestionAssistance'
import {
  StyledStandardSetInputContainer,
  StyledFieldLabel,
  StyledStandardSetDropdownContainer,
  StyledStandardSetTooltip,
  StyledStandardTooltipWrapper,
  StyledStandardSetTooltipContent,
} from '../common/styled'
import { StyledRequired } from '../../Reports/subPages/dataWarehouseReports/GoalsAndInterventions/common/components/Form/styled-components'
import { generateBulkStandardsAction } from '../ducks/bulkQuestionMetadata'
import { METADATA_CONTEXT_FLOW } from '../constants/aiQuestionMetadataGeneration'
import {
  StyledStandardsDrawer,
  StyledDrawerHeader,
  StyledDrawerBody,
  StyledFieldsBlock,
  StyledStandardsSelect,
  StyledGenerateButton,
  StyledDrawerInfoContainer,
  StyledDrawerInfoList,
} from './styled'

const { ALL_PA_GRADES } = pear
const { Option } = Select
const drawerTitleId = 'bulk-standards-drawer-title'

const GenerateStandardsDrawer = ({
  isOpen,
  onClose,
  test,
  testSubjects,
  testGrades,
  interestedCurriculums,
  interestedSubjects,
  interestedGrades,
  generateStandards,
}) => {
  const { _id: testId } = test || {}

  const { subjects: resolvedSubjects, grades: resolvedGrades } = useMemo(
    () =>
      resolveMetadataContext({
        flow: METADATA_CONTEXT_FLOW.TEST_BULK_METADATA_GENERATION,
        testSubjects: testSubjects?.length > 0 ? testSubjects : [],
        testGrades: testGrades?.length > 0 ? testGrades : [],
        interestedSubjects:
          interestedSubjects?.length > 0 ? interestedSubjects : [],
        interestedGrades: interestedGrades?.length > 0 ? interestedGrades : [],
      }),
    [testSubjects, testGrades, interestedSubjects, interestedGrades]
  )

  const standardSetOptions = useMemo(
    () =>
      getStandardSetOptions({
        interestedCurriculums,
        interestedSubjects: resolvedSubjects,
      })?.standardSetOptions || [],
    [interestedCurriculums, resolvedSubjects]
  )

  const [grade, setGrade] = useState(null)
  const [standardSetId, setStandardSetId] = useState(null)
  const rubricApiRef = useRef(null)

  const handleRegisterRubricApi = useCallback((api) => {
    rubricApiRef.current = api
  }, [])

  useEffect(() => {
    if (!isOpen) return
    setGrade(resolvedGrades?.[0] || null)
    setStandardSetId(
      standardSetOptions.length === 1 ? standardSetOptions[0]._id : null
    )
  }, [isOpen, resolvedGrades, standardSetOptions])

  useEffect(() => {
    if (!isOpen) return
    segmentApi.genericEventTrack(
      'AITestQuestionsMetadata:StandardSetInputDisplayed',
      {
        testId,
        is_standard_set_preset: standardSetOptions.length === 1,
        standard_set_count: standardSetOptions.length,
      },
      { attachUserData: true }
    )
  }, [isOpen])

  const handleGenerate = () => {
    const standardSet = standardSetOptions.find((c) => c._id === standardSetId)
    const rubricApi = rubricApiRef.current
    // Selected rubric is stamped onto essay suggestions with the metadata API
    // response (suggestion-only until Save — same as standards).
    const selectedRubricId =
      typeof rubricApi?.getSelectedRubricId === 'function'
        ? rubricApi.getSelectedRubricId()
        : null

    segmentApi.genericEventTrack(
      'AITestQuestionsMetadata:GenerateStandardsClick',
      {
        testId,
        grade,
        curriculumId: standardSetId,
        subject: standardSet?.subject,
      },
      { attachUserData: true }
    )
    onClose()
    generateStandards({
      grade,
      curriculumId: standardSetId,
      subject: standardSet?.subject,
      rubricId: selectedRubricId || null,
    })
  }

  return (
    <StyledStandardsDrawer
      title={null}
      placement="right"
      width={440}
      closable={false}
      onClose={onClose}
      visible={isOpen}
      destroyOnClose
      role="dialog"
      aria-modal="true"
      aria-labelledby={drawerTitleId}
    >
      <StyledDrawerHeader>
        <div className="drawer-header-left">
          <IconPDLSparkle width={24} height={24} aria-hidden="true" />
          <span className="drawer-title" id={drawerTitleId}>
            Pear Assist
          </span>
        </div>
        <button
          type="button"
          className="drawer-close"
          data-cy="bulkStandardsClose"
          aria-label="Close Pear Assist"
          onClick={onClose}
        >
          <IconClose width={14} height={14} color="#151d34" />
        </button>
      </StyledDrawerHeader>
      <StyledDrawerBody>
        <StyledFieldsBlock>
          <p className="drawer-description">
            Auto fill standards for items missing standards using Pear Assist
            AI. Please confirm the standard set before proceeding
          </p>
          <div className="drawer-divider" />
          <StyledStandardSetDropdownContainer>
            <StyledStandardSetInputContainer>
              <StyledFieldLabel>
                <span>
                  Grade<StyledRequired>*</StyledRequired>
                </span>
              </StyledFieldLabel>
            </StyledStandardSetInputContainer>
            <StyledStandardsSelect
              data-cy="bulkStandardsGrade"
              placeholder="Select grade"
              value={grade || undefined}
              onChange={setGrade}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              {ALL_PA_GRADES.map(({ code, description }) => (
                <Option key={code} value={code}>
                  {description}
                </Option>
              ))}
            </StyledStandardsSelect>
          </StyledStandardSetDropdownContainer>
          <StyledStandardSetDropdownContainer>
            <StyledStandardSetInputContainer>
              <StyledFieldLabel>
                <span>
                  Standard Set<StyledRequired>*</StyledRequired>
                </span>
              </StyledFieldLabel>
              <StyledStandardSetTooltip
                title={
                  <StyledStandardSetTooltipContent>
                    The standard sets listed are based on your preferred
                    standards. You can update these preferences from your
                    profile page.
                  </StyledStandardSetTooltipContent>
                }
              >
                <StyledStandardTooltipWrapper>
                  <IconBlueQuestionCircle
                    width="16px"
                    height="16px"
                    aria-hidden="true"
                  />
                </StyledStandardTooltipWrapper>
              </StyledStandardSetTooltip>
            </StyledStandardSetInputContainer>
            <StyledStandardsSelect
              data-cy="bulkStandardsSet"
              placeholder="Select standard set"
              value={standardSetId || undefined}
              onChange={setStandardSetId}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              {standardSetOptions.map((curriculum) => (
                <Option key={curriculum._id} value={curriculum._id}>
                  {curriculum.curriculum || curriculum.name}
                </Option>
              ))}
            </StyledStandardsSelect>
          </StyledStandardSetDropdownContainer>
          <PearAssistRubricSection
            isOpen={isOpen}
            onRegisterApi={handleRegisterRubricApi}
          />
        </StyledFieldsBlock>
        <StyledDrawerInfoContainer data-cy="bulkStandardsInfoContainer">
          <StyledDrawerInfoList>
            <li>
              Pear Assist will process all items you can edit. For items you can
              clone, it will automatically create a copy when you save
              suggestions.
            </li>
            <li>Rubric will be applied only to essay types.</li>
            <li>Items you cannot edit or clone will not be processed.</li>
          </StyledDrawerInfoList>
        </StyledDrawerInfoContainer>
        <StyledGenerateButton
          type="button"
          data-cy="bulkStandardsGenerate"
          disabled={!grade || !standardSetId}
          onClick={handleGenerate}
        >
          Continue
        </StyledGenerateButton>
      </StyledDrawerBody>
    </StyledStandardsDrawer>
  )
}

GenerateStandardsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  generateStandards: PropTypes.func.isRequired,
  test: PropTypes.object,
}

GenerateStandardsDrawer.defaultProps = {
  test: {},
}

export default connect(
  (state) => ({
    test: getTestEntitySelector(state),
    testSubjects: getResolvedTestSubjectsSelector(state),
    testGrades: getResolvedTestGradesSelector(state),
    interestedCurriculums: getAllInterestedCurriculumsSelector(state),
    interestedSubjects: getInterestedSubjectsSelector(state),
    interestedGrades: getInterestedGradesSelector(state),
  }),
  {
    generateStandards: generateBulkStandardsAction,
  }
)(GenerateStandardsDrawer)
